import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Container, ReportScoringBandsLegend, SimpleButton, Loading, ErrorMessage } from '@/src/ui';
import ClassMatrixReportFilters from './components/ClassMatrixFilters';
import ClassMatrixReportDataTable from './components/ClassMatrixTable';
import ClassMatrixLegend from './components/ClassMatrixLegend';
import type { ClassMatrixDTO } from './models/classMatrixDTOs';
import type { ClassMatrixReportFilterState } from './models/filterDTOs';
import QuestionDetailsModal from './components/QuestionDetailsModal';
import { useAuth } from '@/src/app/context/useAuth';

import './ClassMatrixPage.css';

const ClassMatrixReportPage: React.FC = () => {
    document.title = 'Class Matrix Report';

    const [data, setData] = useState<ClassMatrixDTO | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [showMatrixLegend, setShowMatrixLegend] = useState(false);
    const [showReportingBands, setShowReportingBands] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<ClassMatrixReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);

    const handleQuestionClick = (questionId: number) => {
        setSelectedQuestionId(Number(questionId));
        setModalOpen(true);
    };

    const refreshTableData = useCallback(
        async (filters: ClassMatrixReportFilterState = filterState) => {
            setError('');
            setLoaded(false);

            if (!filters.districtId || !filters.schoolId || !filters.classId || !filters.examId) {
                setError('Please select Grade, District, School, Class, and Exam.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/classMatrix?districtId=${filters.districtId}`;
            requestUrl += `&schoolId=${filters.schoolId}`;
            requestUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
            requestUrl += `&classId=${filters.classId}`;
            requestUrl += `&examId=${filters.examId}`;
            requestUrl += `&examTypeMasterId=${filters.examTypeMasterId}`;

            try {
                const res = await apiRequest<ClassMatrixDTO>('GET', requestUrl);
                setData(res.data ?? null);
            } catch {
                setError('Failed to load Class Matrix Report');
                setData(null);
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        setData(null);
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId && fs.classId && fs.examId) {
            refreshTableData();
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dataLoaded = data !== null && data.questions.length !== 0;

    return (
        <Container>
            <h1 className="page-header">Class Matrix Report</h1>
            <br />

            <Box>
                <ClassMatrixReportFilters
                    filterState={filterState}
                    setFilterState={setFilterState}
                    refreshTableData={refreshTableData}
                />
            </Box>

            <Loading visible={!loaded} message={'Loading Class Matrix Report...'} />
            <ErrorMessage error={error} style={{ textAlign: 'center', marginBottom: '8px' }} />

            <Box sx={{ pb: 2 }}>
                {/* Button row */}
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    {dataLoaded && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                            }}
                        >
                            <SimpleButton
                                variant="outlined"
                                onClick={() => setShowMatrixLegend(!showMatrixLegend)}
                                aria-expanded={showMatrixLegend}
                                aria-label="Toggle legend dropdown"
                            >
                                {showMatrixLegend ? 'Hide Legend' : 'Show Legend'}
                            </SimpleButton>
                            <SimpleButton
                                variant="outlined"
                                onClick={() => setShowReportingBands(!showReportingBands)}
                                aria-expanded={showReportingBands}
                                aria-label="Toggle reporting bands dropdown"
                            >
                                {showReportingBands ? 'Hide Scoring Bands' : 'Show Scoring Bands'}
                            </SimpleButton>
                        </Box>
                    )}

                    {filterState.examId !== null && filterState.examId !== 0 && (
                        <Button
                            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                            variant="outlined"
                            href={`/reports/questionList/${filterState.examId}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View Assessment
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <ClassMatrixLegend dataLoaded={dataLoaded} showLegend={showMatrixLegend} />
                    <ReportScoringBandsLegend
                        dataLoaded={dataLoaded}
                        showLegend={showReportingBands}
                    />
                </Box>
            </Box>

            <div className="table-container" style={{ overflow: 'auto' }}>
                {loaded && data && (
                    <ClassMatrixReportDataTable data={data} onQuestionClick={handleQuestionClick} />
                )}
            </div>

            {selectedQuestionId && (
                <QuestionDetailsModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    filterState={filterState}
                    questionId={selectedQuestionId}
                />
            )}
        </Container>
    );
};

export default ClassMatrixReportPage;
